<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("BUTTONS.add_notification") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: var(--main_theme_clr)">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <div class="col-12">
            <div class="row">
              <!-- Start:: Title Ar Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.titleAr')"
                v-model.trim="data.titleAr"
                required
              />
              <!-- End:: Title Ar Input -->

              <!-- Start:: Title En Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.titleEn')"
                v-model.trim="data.titleEn"
                required
              />
              <!-- End:: Title En Input -->

              <!-- Start:: Content Ar Input -->
              <base-input
                col="6"
                rows="7"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.contentAr')"
                v-model.trim="data.contentAr"
                required
              />
              <!-- End:: Content Ar Input -->

              <!-- Start:: Content En Input -->
              <base-input
                col="6"
                rows="7"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.contentEn')"
                v-model.trim="data.contentEn"
                required
              />
              <!-- End:: Content En Input -->
            </div>
          </div>

          <!-- Start:: Recipient Types Multi-Select -->
          <base-select-input
            col="6"
            :optionsList="recipientTypes"
            :placeholder="$t('PLACEHOLDERS.recipientTypes')"
            v-model="data.selectedRecipientTypes"
            required
            multiple
            track-by="value"
          />
          <!-- End:: Recipient Types Multi-Select -->

          <!-- Start:: Recipients Multi-Select -->
          <base-select-input
            v-if="data.selectedRecipientTypes.length > 0 && !isAllSelected"
            col="6"
            :optionsList="availableRecipients"
            :placeholder="$t('PLACEHOLDERS.recipients')"
            v-model="data.selectedRecipients"
            multiple
            trackBy="id"
            label="name"
          />
          <!-- End:: Recipients Multi-Select -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
export default {
  name: "CreateNotification",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        selectedRecipientTypes: [],
        selectedRecipients: [],
        titleAr: null,
        titleEn: null,
        contentAr: null,
        contentEn: null,
      },
      // End:: Data Collection To Send

      // Start:: Recipients Data
      clients: [],
      providers: [],
      // End:: Recipients Data
    };
  },

  computed: {
    recipientTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.serviceClients"),
          value: "client",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.serviceProviders"),
          value: "provider",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.all"),
          value: "all",
        },
      ];
    },

    isAllSelected() {
      return this.data.selectedRecipientTypes.some(
        (type) => type.value === "all"
      );
    },

    realRecipients() {
      let recipients = [];

      this.data.selectedRecipientTypes.forEach((type) => {
        if (type.value === "client") {
          recipients = recipients.concat(
            this.clients.map((client) => ({
              ...client,
              type: "client",
            }))
          );
        } else if (type.value === "provider") {
          recipients = recipients.concat(
            this.providers.map((provider) => ({
              ...provider,
              type: "provider",
            }))
          );
        }
      });

      return recipients;
    },

    availableRecipients() {
      if (!this.realRecipients.length) return [];
      return [
        {
          id: "__all__",
          name: this.$t("PLACEHOLDERS.all"),
          isSelectAll: true,
        },
        ...this.realRecipients,
      ];
    },
  },

  watch: {
    "data.selectedRecipientTypes": {
      handler(newVal) {
        // Clear selected recipients when types change
        this.data.selectedRecipients = [];

        // If "all" is selected along with other values, keep only the selected "all" item
        if (newVal.some((type) => type.value === "all") && newVal.length > 1) {
          this.data.selectedRecipientTypes = newVal.filter(
            (type) => type.value === "all"
          );
        }
      },
      deep: true,
    },

    "data.selectedRecipients": {
      handler(newVal) {
        if (!Array.isArray(newVal) || !newVal.length) return;
        const hasSelectAll = newVal.some(
          (item) => item?.isSelectAll || item?.id === "__all__"
        );
        if (!hasSelectAll) return;

        const allRecipients = this.realRecipients;
        const currentIds = newVal
          .filter((item) => !item?.isSelectAll && item?.id !== "__all__")
          .map((item) => item.id)
          .join(",");
        const allIds = allRecipients.map((item) => item.id).join(",");
        if (currentIds === allIds) {
          this.data.selectedRecipients = allRecipients;
          return;
        }
        this.data.selectedRecipients = allRecipients;
      },
      deep: true,
    },
  },

  methods: {
    async getClients() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `get-all-users/client`,
        });
        this.clients = res.data.data.users || [];
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },

    async getProviders() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `get-all-users/provider`,
        });
        this.providers = res.data.data.users || [];
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;

      if (this.data.selectedRecipientTypes.length === 0) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.recipientType"));
        return;
      } else if (!this.data.titleAr) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.titleAr"));
        return;
      } else if (!this.data.titleEn) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.titleEn"));
        return;
      } else if (!this.data.contentAr) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.contentAr"));
        return;
      } else if (!this.data.contentEn) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.contentEn"));
        return;
      } else {
        this.submitForm();
        return;
      }
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = {
        title: {
          ar: this.data.titleAr,
          en: this.data.titleEn,
        },
        body: {
          ar: this.data.contentAr,
          en: this.data.contentEn,
        },
        targets: this.data.selectedRecipientTypes.map((type) => type.value),
        ids: this.data.selectedRecipients
          .filter((recipient) => !recipient?.isSelectAll && recipient?.id !== "__all__")
          .map((recipient) => recipient.id),
      };

      try {
        await this.$axios({
          method: "POST",
          url: `notifications/notification-content`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.sentSuccessfully"));
        this.$router.push({ path: "/all-notifications/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response?.data?.message);
      }
    },
    // End:: Submit Form
  },

  created() {
    // Start:: Fire Methods
    this.getClients();
    this.getProviders();
    // End:: Fire Methods
  },
};
</script>
